/**
 * Untracked-counterparty reason + owner confirm string — browser-safe.
 *
 * The admin portal cannot import `untracked_counterparty.ts` (HttpError, Env).
 * Worker DB writers re-export from here. Keep this file free of Worker HTTP.
 *
 * GRANT — owner 2026-09-08, verbatim: "Approved, brief it."
 * The confirmation string is the owner's words and is not ours to improve.
 */

import {
  amVisibleMultilineDisplayText,
  replaceAmVisibleLineHazards,
} from "./am_visible_text";

export const UNTRACKED_COUNTERPARTY_REASON = "untracked-counterparty" as const;

/** Owner's confirmation string — not ours to improve. */
export const UNTRACKED_COUNTERPARTY_ONBOARD_CONFIRM =
  "This will move to a task to fully onboard this company's financials - Confirm";

/**
 * Prefix of the confirm-alias note. The AM picks a company from the chooser;
 * the portal posts this prefix plus that slug. Not inferred from
 * mark-external or create-company.
 */
export const UNTRACKED_COUNTERPARTY_ALIAS_CONFIRM_PREFIX =
  "This label is company " as const;

/** Stale untracked-counterparty click — Law 2 generic; no field names. */
export const CARD_CHANGED_MESSAGE = "This card has changed. Review it again.";

const ALIAS_CONFIRM_SLUG_RE = /^[a-z][a-z0-9-]{0,63}$/;

export type UntrackedCounterpartyDisposition =
  | "mark-external"
  | "create-company"
  | "confirm-alias";

export function hubLabelsFromRationale(
  aiRationale: string | null | undefined,
): { fromLabel: string | null; toLabel: string | null } {
  if (typeof aiRationale !== "string") {
    return { fromLabel: null, toLabel: null };
  }
  // Historical rows may still carry U+2028 in a from:/to: value; treat
  // those as hazards inside the line, not as extra rationale lines.
  const cleaned = amVisibleMultilineDisplayText(aiRationale);
  const fromMatch = /^from:(.+)$/m.exec(cleaned);
  const toMatch = /^to:(.+)$/m.exec(cleaned);
  const fromLabel = fromMatch?.[1]?.trim() ?? "";
  const toLabel = toMatch?.[1]?.trim() ?? "";
  return {
    fromLabel: fromLabel.length > 0 ? fromLabel : null,
    toLabel: toLabel.length > 0 ? toLabel : null,
  };
}

/**
 * Hub labels are untrusted. Compose {@link replaceAmVisibleLineHazards} so
 * durable alias/recognition identity matches the reviewed AM-visible
 * representation (R38A-3). Trim and clip to companies.display_name / OpenAPI
 * max (200). Not a hub-admission change and not a CHECK change — the hub
 * still accepts the raw string; this is how it becomes durable. Do not
 * delete newlines (that silently joined words); they become U+FFFD.
 */
export function clipCounterpartyAliasText(value: string): string {
  return replaceAmVisibleLineHazards(value.trim()).slice(0, 200);
}

export function untrackedCounterpartyAliasNote(slug: string): string {
  return `${UNTRACKED_COUNTERPARTY_ALIAS_CONFIRM_PREFIX}${slug}`;
}

export function slugFromUntrackedCounterpartyAliasNote(
  note: string,
): string | null {
  if (!note.startsWith(UNTRACKED_COUNTERPARTY_ALIAS_CONFIRM_PREFIX)) {
    return null;
  }
  const slug = note.slice(UNTRACKED_COUNTERPARTY_ALIAS_CONFIRM_PREFIX.length);
  if (!ALIAS_CONFIRM_SLUG_RE.test(slug)) return null;
  return slug;
}

/**
 * Closed note → disposition map. Omitted/empty note is mark-external; the
 * owner's confirm string is create-company; the alias prefix plus a valid
 * slug is confirm-alias. Anything else is unrecognised (null) — do not
 * default it. Confirm-alias is a distinct AM answer, not inferred from
 * the other two.
 */
export function untrackedCounterpartyDispositionFromNote(
  note: string | undefined,
): UntrackedCounterpartyDisposition | null {
  if (note === undefined || note === "") {
    return "mark-external";
  }
  if (note === UNTRACKED_COUNTERPARTY_ONBOARD_CONFIRM) {
    return "create-company";
  }
  if (slugFromUntrackedCounterpartyAliasNote(note) !== null) {
    return "confirm-alias";
  }
  return null;
}

export function slugFromCounterpartyLabel(label: string): string {
  const folded = label.toLowerCase();
  const slim = folded.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (slim.length === 0) return "x-counterparty";
  const withLetter = /^[a-z]/.test(slim) ? slim : `x-${slim}`;
  const clipped = withLetter.slice(0, 64).replace(/-+$/g, "");
  return clipped.length > 0 ? clipped : "x-counterparty";
}

export function untrackedCounterpartyRationale(
  fromLabel: string,
  toLabel: string,
): string {
  const from = clipCounterpartyAliasText(fromLabel);
  const to = clipCounterpartyAliasText(toLabel);
  return `untracked-counterparty\nfrom:${from}\nto:${to}`;
}

export function counterpartyLabelFromRationale(
  aiRationale: string | null | undefined,
  merchant: string | null | undefined,
): string {
  if (typeof merchant === "string" && merchant.length > 0) {
    return replaceAmVisibleLineHazards(merchant);
  }
  const labels = hubLabelsFromRationale(aiRationale);
  if (labels.toLabel !== null) return labels.toLabel;
  if (labels.fromLabel !== null) return labels.fromLabel;
  return "this counterparty";
}
